// Reusable, config-driven layout model for gridstack panel dialogs (package + mapping profile + vehicle).

export interface PanelGridPlacement {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface PanelGridConfig {
  schemaVersion: number;
  columns: number;
  cellHeight: number;
  margin: number;
  collapsedRows: number;
  window: {
    defaultWidth: number;
    defaultHeight: number;
    minWidth: number;
    minHeight: number;
    maxWidth: number;
    maxHeight: number;
  };
  label: {
    default: number;
    min: number;
    max: number;
  };
  panelOrder: string[];
  placements: Record<string, PanelGridPlacement>;
}

export interface PanelGridGroup {
  id: string;
  // Panels shown as tabs within this grid tile.
  panels: string[];
  activePanelId: string;
  x: number;
  y: number;
  w: number;
  h: number;
  collapsed: boolean;
  // Height in grid rows to restore when a collapsed tile is expanded again.
  expandedHeight: number;
}

export interface PanelGridWindowState {
  width: number;
  height: number;
  left: number | null;
  top: number | null;
}

export interface PanelGridFieldSettings {
  // Shared label-column width (px) applied to every field in the panel's form grid.
  labelWidth: number;
  // Field keys hidden by the row picker.
  hidden: string[];
}

export type PanelGridFieldSettingsMap = Record<string, PanelGridFieldSettings>;

export interface PanelGridTabsheet {
  id: string;
  name: string;
  groups: PanelGridGroup[];
}

export interface PanelGridLayoutState {
  schemaVersion: number;
  window: PanelGridWindowState;
  tabsheets: PanelGridTabsheet[];
  activeTabsheetId: string;
  fieldSettings: PanelGridFieldSettingsMap;
}

export interface StoredPanelGridGroup {
  id?: string;
  panels?: string[] | null;
  activePanelId?: string;
  x?: number;
  y?: number;
  w?: number;
  h?: number;
  collapsed?: boolean;
  expandedHeight?: number;
}

export interface StoredPanelGridTabsheet {
  id?: string;
  name?: string;
  groups?: StoredPanelGridGroup[] | null;
}

export interface StoredPanelGridFieldSettings {
  labelWidth?: number;
  hidden?: string[] | null;
}

export interface StoredPanelGridLayoutState {
  schemaVersion?: number;
  window?: Partial<PanelGridWindowState> | null;
  tabsheets?: StoredPanelGridTabsheet[] | null;
  activeTabsheetId?: string;
  fieldSettings?: Record<string, StoredPanelGridFieldSettings> | null;
}

const fallbackPlacement: PanelGridPlacement = { x: 0, y: 0, w: 1, h: 1 };

export function createPanelGridGroups(config: PanelGridConfig): PanelGridGroup[] {
  return config.panelOrder.map((id) => {
    const placement = config.placements[id] ?? fallbackPlacement;
    return {
      id: `group-${id}`,
      panels: [id],
      activePanelId: id,
      ...placement,
      collapsed: false,
      expandedHeight: placement.h,
    };
  });
}

export function createPanelGridFieldSettings(config: PanelGridConfig): PanelGridFieldSettingsMap {
  return config.panelOrder.reduce((map, id) => {
    map[id] = { labelWidth: config.label.default, hidden: [] };
    return map;
  }, {} as PanelGridFieldSettingsMap);
}

export function createPanelGridLayout(config: PanelGridConfig): PanelGridLayoutState {
  return {
    schemaVersion: config.schemaVersion,
    window: {
      width: config.window.defaultWidth,
      height: config.window.defaultHeight,
      left: null,
      top: null,
    },
    tabsheets: [{ id: 'sheet-1', name: 'Sheet 1', groups: createPanelGridGroups(config) }],
    activeTabsheetId: 'sheet-1',
    fieldSettings: createPanelGridFieldSettings(config),
  };
}

export function migratePanelGridLayout(
  config: PanelGridConfig,
  stored?: StoredPanelGridLayoutState | null,
): PanelGridLayoutState {
  if (!stored || stored.schemaVersion !== config.schemaVersion) {
    return createPanelGridLayout(config);
  }

  const tabsheets = normalizeTabsheets(config, stored.tabsheets);
  const activeTabsheetId = tabsheets.some((sheet) => sheet.id === stored.activeTabsheetId)
    ? (stored.activeTabsheetId as string)
    : (tabsheets[0]?.id ?? 'sheet-1');

  return {
    schemaVersion: config.schemaVersion,
    window: normalizeWindow(config, stored.window),
    tabsheets,
    activeTabsheetId,
    fieldSettings: normalizeFieldSettings(config, stored.fieldSettings),
  };
}

// Move a whole tile (with its tabs) out of its current sheet into a brand-new sheet.
export function movePanelGridGroupToNewTabsheet(
  tabsheets: PanelGridTabsheet[],
  groupId: string,
): { tabsheets: PanelGridTabsheet[]; newTabsheetId: string } {
  let group: PanelGridGroup | null = null;
  for (const sheet of tabsheets) {
    const found = sheet.groups.find((entry) => entry.id === groupId);
    if (found) {
      group = found;
      break;
    }
  }
  if (!group) {
    return { tabsheets, newTabsheetId: '' };
  }

  const existingSheetIds = new Set(tabsheets.map((sheet) => sheet.id));
  const newId = uniqueTabsheetId(existingSheetIds);
  const movedGroup: PanelGridGroup = { ...group, x: 0, y: 0 };
  const newSheet: PanelGridTabsheet = {
    id: newId,
    name: nextTabsheetName(tabsheets),
    groups: [movedGroup],
  };

  const updated = tabsheets
    .map((sheet) => ({ ...sheet, groups: sheet.groups.filter((entry) => entry.id !== groupId) }))
    .filter((sheet) => sheet.groups.length > 0)
    .concat(newSheet);

  return { tabsheets: updated, newTabsheetId: newId };
}

// Move a whole tile from its current sheet into an existing target sheet.
export function movePanelGridGroupToTabsheet(
  tabsheets: PanelGridTabsheet[],
  groupId: string,
  targetSheetId: string,
): PanelGridTabsheet[] {
  let group: PanelGridGroup | null = null;
  let sourceSheetId: string | null = null;
  for (const sheet of tabsheets) {
    const found = sheet.groups.find((entry) => entry.id === groupId);
    if (found) {
      group = found;
      sourceSheetId = sheet.id;
      break;
    }
  }
  const target = tabsheets.find((sheet) => sheet.id === targetSheetId);
  if (!group || !target || sourceSheetId === targetSheetId) {
    return tabsheets;
  }

  // Drop the tile below whatever the target sheet already holds so it doesn't overlap.
  const bottom = target.groups.reduce((max, entry) => Math.max(max, entry.y + entry.h), 0);
  const movedGroup: PanelGridGroup = { ...group, x: 0, y: bottom };

  return tabsheets
    .map((sheet) => {
      if (sheet.id === sourceSheetId) {
        return { ...sheet, groups: sheet.groups.filter((entry) => entry.id !== groupId) };
      }
      if (sheet.id === targetSheetId) {
        return { ...sheet, groups: [...sheet.groups, movedGroup] };
      }
      return sheet;
    })
    .filter((sheet) => sheet.groups.length > 0);
}

export function reorderPanelGridTabsheets(
  tabsheets: PanelGridTabsheet[],
  fromId: string,
  toId: string,
): PanelGridTabsheet[] {
  const fromIndex = tabsheets.findIndex((sheet) => sheet.id === fromId);
  const toIndex = tabsheets.findIndex((sheet) => sheet.id === toId);
  if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
    return tabsheets;
  }
  const next = [...tabsheets];
  const [moved] = next.splice(fromIndex, 1);
  if (!moved) {
    return tabsheets;
  }
  next.splice(toIndex, 0, moved);
  return next;
}

function uniqueTabsheetId(existing: Set<string>): string {
  let counter = existing.size + 1;
  let id = `sheet-${counter}`;
  while (existing.has(id)) {
    counter += 1;
    id = `sheet-${counter}`;
  }
  return id;
}

function nextTabsheetName(tabsheets: PanelGridTabsheet[]): string {
  const names = new Set(tabsheets.map((sheet) => sheet.name));
  let counter = tabsheets.length + 1;
  while (names.has(`Sheet ${counter}`)) {
    counter += 1;
  }
  return `Sheet ${counter}`;
}

// Merge the source tile's panels into the target tile as extra tabs and drop the source tile.
export function mergePanelGridGroups(
  groups: PanelGridGroup[],
  sourceId: string,
  targetId: string,
): PanelGridGroup[] {
  if (sourceId === targetId) {
    return groups;
  }
  const source = groups.find((group) => group.id === sourceId);
  const target = groups.find((group) => group.id === targetId);
  if (!source || !target) {
    return groups;
  }

  const mergedPanels = [...target.panels, ...source.panels.filter((panel) => !target.panels.includes(panel))];
  return groups
    .filter((group) => group.id !== sourceId)
    .map((group) =>
      group.id === targetId
        ? { ...group, panels: mergedPanels, activePanelId: source.activePanelId }
        : group,
    );
}

// Pop a single panel out of a tab group into its own tile.
export function detachPanelGridPanel(
  config: PanelGridConfig,
  groups: PanelGridGroup[],
  groupId: string,
  panelId: string,
): PanelGridGroup[] {
  const group = groups.find((entry) => entry.id === groupId);
  if (!group || group.panels.length <= 1 || !group.panels.includes(panelId)) {
    return groups;
  }

  const remaining = group.panels.filter((panel) => panel !== panelId);
  const existingIds = new Set(groups.map((entry) => entry.id));
  const newGroup: PanelGridGroup = {
    id: uniqueGroupId(panelId, existingIds),
    panels: [panelId],
    activePanelId: panelId,
    x: Math.min(group.x + 1, config.columns - 1),
    y: group.y + 1,
    w: group.w,
    h: group.expandedHeight,
    collapsed: false,
    expandedHeight: group.expandedHeight,
  };

  return groups
    .map((entry) =>
      entry.id === groupId
        ? {
            ...entry,
            panels: remaining,
            activePanelId: remaining.includes(entry.activePanelId) ? entry.activePanelId : (remaining[0] ?? entry.activePanelId),
          }
        : entry,
    )
    .concat(newGroup);
}

function uniqueGroupId(base: string, existing: Set<string>): string {
  let id = `group-${base}`;
  let counter = 1;
  while (existing.has(id)) {
    id = `group-${base}-${counter}`;
    counter += 1;
  }
  return id;
}

function normalizeFieldSettings(
  config: PanelGridConfig,
  stored: StoredPanelGridLayoutState['fieldSettings'],
): PanelGridFieldSettingsMap {
  return config.panelOrder.reduce((map, id) => {
    const persisted = stored?.[id];
    const hidden = Array.isArray(persisted?.hidden)
      ? persisted!.hidden!.filter((key): key is string => typeof key === 'string')
      : [];
    map[id] = {
      labelWidth: normalizeInt(persisted?.labelWidth, config.label.min, config.label.max, config.label.default),
      hidden: [...new Set(hidden)],
    };
    return map;
  }, {} as PanelGridFieldSettingsMap);
}

function normalizeWindow(config: PanelGridConfig, state: StoredPanelGridLayoutState['window']): PanelGridWindowState {
  return {
    width: normalizeInt(state?.width, config.window.minWidth, config.window.maxWidth, config.window.defaultWidth),
    height: normalizeInt(state?.height, config.window.minHeight, config.window.maxHeight, config.window.defaultHeight),
    left: typeof state?.left === 'number' && Number.isFinite(state.left) ? Math.round(state.left) : null,
    top: typeof state?.top === 'number' && Number.isFinite(state.top) ? Math.round(state.top) : null,
  };
}

function normalizeTabsheets(config: PanelGridConfig, stored: StoredPanelGridLayoutState['tabsheets']): PanelGridTabsheet[] {
  const seen = new Set<string>();
  const usedGroupIds = new Set<string>();
  const usedSheetIds = new Set<string>();
  const sheets: PanelGridTabsheet[] = [];

  for (const entry of stored ?? []) {
    const groups = normalizeGroupList(config, entry?.groups, seen, usedGroupIds);
    if (groups.length === 0) {
      continue;
    }
    const id = typeof entry?.id === 'string' && entry.id && !usedSheetIds.has(entry.id)
      ? entry.id
      : uniqueTabsheetId(usedSheetIds);
    usedSheetIds.add(id);
    const name = typeof entry?.name === 'string' && entry.name.trim() ? entry.name.trim() : `Sheet ${sheets.length + 1}`;
    sheets.push({ id, name, groups });
  }

  // Recover any panel missing from persisted data into the first sheet.
  const recovered: PanelGridGroup[] = [];
  for (const panelId of config.panelOrder) {
    if (!seen.has(panelId)) {
      const placement = config.placements[panelId] ?? fallbackPlacement;
      const id = uniqueGroupId(panelId, usedGroupIds);
      usedGroupIds.add(id);
      recovered.push({
        id,
        panels: [panelId],
        activePanelId: panelId,
        ...placement,
        collapsed: false,
        expandedHeight: placement.h,
      });
    }
  }

  if (recovered.length > 0) {
    if (sheets.length === 0) {
      const id = uniqueTabsheetId(usedSheetIds);
      usedSheetIds.add(id);
      sheets.push({ id, name: 'Sheet 1', groups: recovered });
    } else {
      const first = sheets[0];
      if (first) {
        sheets[0] = { ...first, groups: [...first.groups, ...recovered] };
      }
    }
  }

  return sheets.length > 0
    ? sheets
    : [{ id: 'sheet-1', name: 'Sheet 1', groups: createPanelGridGroups(config) }];
}

function normalizeGroupList(
  config: PanelGridConfig,
  stored: StoredPanelGridGroup[] | null | undefined,
  seen: Set<string>,
  usedIds: Set<string>,
): PanelGridGroup[] {
  const groups: PanelGridGroup[] = [];

  for (const entry of stored ?? []) {
    const panels: string[] = [];
    for (const panel of entry?.panels ?? []) {
      if (isPanelId(config, panel) && !seen.has(panel) && !panels.includes(panel)) {
        panels.push(panel);
        seen.add(panel);
      }
    }
    if (panels.length === 0) {
      continue;
    }

    const firstPanel = panels[0]!;
    const activePanelId = isPanelId(config, entry?.activePanelId) && panels.includes(entry!.activePanelId as string)
      ? (entry!.activePanelId as string)
      : firstPanel;
    const placement = config.placements[firstPanel] ?? fallbackPlacement;
    const collapsed = typeof entry?.collapsed === 'boolean' ? entry.collapsed : false;
    const expandedHeight = normalizeInt(entry?.expandedHeight, 1, 60, placement.h);
    const id = typeof entry?.id === 'string' && entry.id && !usedIds.has(entry.id)
      ? entry.id
      : uniqueGroupId(firstPanel, usedIds);
    usedIds.add(id);

    groups.push({
      id,
      panels,
      activePanelId,
      x: normalizeInt(entry?.x, 0, config.columns - 1, placement.x),
      y: normalizeInt(entry?.y, 0, 400, placement.y),
      w: normalizeInt(entry?.w, 1, config.columns, placement.w),
      h: collapsed ? config.collapsedRows : normalizeInt(entry?.h, 1, 60, placement.h),
      collapsed,
      expandedHeight,
    });
  }

  return groups;
}

function isPanelId(config: PanelGridConfig, value: unknown): value is string {
  return typeof value === 'string' && config.panelOrder.includes(value);
}

function normalizeInt(value: unknown, min: number, max: number, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return fallback;
  }
  return Math.min(Math.max(Math.round(value), min), max);
}
